/*--------------------------------------------------------------------------------------------
Program     :  SignInSide.js
Description :  Main program for Map and Track Login
Date        :  29/08/2024 
Created By  :  Fode/Jelbin
---------------------------------------------------------------------------------------------
TicketNo         Author          Date            ChangeDesc
---------------------------------------------------------------------------------------------
29082024        - Fode/Jelbin     -  29/08/2024        Created
---------------------------------------------------------------------------------------------*/
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import AuthService from "../service/AuthService";
import { Table, TableRow, TableCell } from "@mui/material";


function Copyright(props) {
  return (
    <>
      <Typography variant="body2" color="text.secondary" align="center" {...props}>
        {'Copyright © '}
        <Link color="inherit" href="https://medatechuk.com/">
          Medatech UK
        </Link>{' '}
        {new Date().getFullYear()}
        {'.'}
      </Typography>
      <p></p>
      <Table>
        <TableRow>
          <TableCell
            sx={{
              backgroundImage:
                "url(https://prioritytest.clarksonevans.co.uk/primail/mapandtrack/medatech-transparent-logo.png)",
              backgroundRepeat: "no-repeat",
              backgroundColor: (t) =>
                t.palette.mode === "light"
                  ? t.palette.grey[10]
                  : t.palette.grey[100],
              backgroundSize: "50%",
              height: "10px",
              backgroundPosition: "center",
              border: 'none'
            }}
          ></TableCell>
        </TableRow></Table>
    </>
  );
}

const theme = createTheme();

export default function SignInSide() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [passwordChange, setPasswordChange] = useState(false);
  const [userData, setUserData] = useState({});

  const handleChange = (e) =>
    setUserData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    if (passwordChange) {
      if (data.get('password') === data.get('confirmpassword')) {
        AuthService.changePassword(data)
          .then((response) => {
            if (response.status === 200) {
              enqueueSnackbar(`Successfully changed password! Please login.`, { variant: 'success' });
              setPasswordChange(false);
            } else {
              enqueueSnackbar(`Problem occurred changing password.`, { variant: 'error' });
            }
          });
      } else {
        enqueueSnackbar(`Passwords do not match.`, { variant: 'warning' });
      }
    } else {
      AuthService.login(data)
        .then((response) => {
          if (response.data.newUser === true) {
            enqueueSnackbar(`Please change your password.`, { variant: 'warning' });
            setPasswordChange(true);
          } else {
            enqueueSnackbar(`Successfully logged in!`, { variant: 'success' });
            localStorage.setItem("token", response.data.token);
            localStorage.setItem("UserviewMode", response.data.userType);
            navigate("/Dashboard");
            window.location.reload();
          }
        }).catch(err => {
          enqueueSnackbar("Login Failed", { variant: 'error' });
        });
    }
  };

  const handleForgotPassword = () => {
    AuthService.forgotPassword(userData)
      .then((response) => {
        enqueueSnackbar(`Email has been sent to the contact`, {
          variant: "success",
        });
      })
      .catch((err) => {
        enqueueSnackbar(`User does not exist`, {
          variant: "error",
        });
      });
  };

  return (
    <ThemeProvider theme={theme} sx={{ height: '100vh' }}>

      {/* <Grid container component="main" sx={{ height: '100vh' }}>
        <CssBaseline />
        <Grid
          item
          xs={false}
          sm={4}
          md={7}
          sx={{
            backgroundImage: "url(https://touchstone.medatecherp.co.uk/primail/mapandtrack/map2.png)",
            backgroundRepeat: 'no-repeat',
            backgroundColor: (t) =>
              t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900],
            backgroundSize: 'contain', // This makes the image fit within the container
            backgroundPosition: 'center',
            backgroundSize: '80%' 
          }}
        /> */}
      <Box
        sx={{
          height: '100vh',           // Full viewport height
          width: '100vw',            // Full viewport width
          backgroundImage: "url(https://prioritytest.clarksonevans.co.uk/primail/mapandtrack/newlogin.png)", // Background image URL
          backgroundRepeat: 'no-repeat',   // Prevents the image from repeating
          backgroundSize: 'cover',        // Ensures the image covers the entire box
          backgroundPosition: 'center',    // Centers the image in the box
          display: 'flex',                 // Enables flexbox layout
          justifyContent: 'center',        // Centers children horizontally
          alignItems: 'center',            // Centers children vertically
        }}

      >
        <Box
          sx={{
            display: 'flex',               // Flexbox layout
            marginLeft: '70%',   // Aligns the box to the left side
            width: '100%',                  // Adjust the container's width
            p: 2                            // Padding around the outer container
          }}
        >
          <Box
            sx={{
              width: '300px',               // Adjust the width as needed
              bgcolor: 'whitesmoke',             // Background color for the form box
              borderRadius: '8px',          // Rounded corners
              boxShadow: 3,                 // Shadow effect
              p: 40,                          // Padding
              display: 'flex',              // Enables flexbox layout for the form
              flexDirection: 'column',      // Aligns children vertically
              alignItems: 'center',          // Centers children horizontally
              p: 2,
            }}
            component={Paper}
          >
            <Avatar sx={{ m: 1, bgcolor: '#00C7FE' }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              {passwordChange ? 'Change Password' : 'Sign in'}
            </Typography>
            <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                autoComplete="username"
                onChange={handleChange}
                sx={{ pointerEvents: passwordChange ? 'none' : 'auto' }}
                autoFocus
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label={passwordChange ? "New Password" : "Password"}
                type="password"
                id="password"
                autoComplete="current-password"
                onChange={handleChange}
              />
              {passwordChange && (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmpassword"
                  label="Confirm Password"
                  type="password"
                  id="confirmpassword"
                  onChange={handleChange}
                />
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, backgroundColor: '#00C7FE' }}
              >
                {passwordChange ? 'Change Password' : 'Sign In'}
              </Button>
              <Button
                onClick={handleForgotPassword}
                fullWidth
                sx={{ mt: 3, color: '#00C7FE' }}
              >
                Forgot password?
              </Button>
              <Copyright sx={{ mt: 5 }} />
            </Box>
          </Box>
        </Box>  </Box>
      {/* </Grid> */}
    </ThemeProvider>
  );
}